import React, { useState, useEffect } from "react";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import {
  getMsgTemplatesApi,
  updateMsgTemplatesApi,
} from "../../../services/tenantSetup.service";
import "./MessageTemplate.css";
import { MessageCircleReplyIcon } from "lucide-react";

const MAX_LENGTH = 300;

// ─── TemplateCard ─────────────────────────────────────────────────────────────

const TemplateCard = ({ template, value, onChange }) => {
  const len = value?.length ?? 0;
  const isEmpty = !value || value.trim() === "";

  return (
    <div className={`mts-card${!isEmpty ? " mts-card--filled" : ""}`}>
      <div className="mts-card__header">
        <div
          className="mts-card__icon"
        >
          <MessageCircleReplyIcon/>
        </div>
        <div className="mts-card__meta">
          <span className="mts-card__label">{template.label}</span>
          <span className="mts-card__tooltip">{template.tooltip}</span>
        </div>
      </div>

      <textarea
        className="mts-card__textarea"
        rows={3}
        placeholder={template.placeholder}
        maxLength={MAX_LENGTH}
        value={value ?? ""}
        onChange={(e) => onChange(template.key, e.target.value)}
        aria-label={template.label}
      />

      <div
        className={`mts-card__counter${
          len > MAX_LENGTH - 20 ? " mts-card__counter--error" : len > 240 ? " mts-card__counter--warn" : ""
        }`}
      >
        {len > 0 ? `${len} / ${MAX_LENGTH}` : ""}
      </div>
    </div>
  );
};

// ─── GroupSection ─────────────────────────────────────────────────────────────

const GroupSection = ({ group, groupIndex, values, onChange }) => {
  const [open, setOpen] = useState(groupIndex === 0);

  return (
    <div className="mts-group">
      <button
        className="mts-group__header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="mts-group__title-row">
          <span className="mts-group__dot" />
          <span className="mts-group__title">{group.title}</span>
          <span className="mts-group__count">{group.templates.length}</span>
        </div>
        <span className={`mts-group__chevron${open ? " mts-group__chevron--open" : ""}`}>
          ⌄
        </span>
      </button>

      {open && (
        <div className="mts-group__body">
          {group.templates.map((tpl) => (
            <TemplateCard
              key={tpl.key}
              template={tpl}
              value={values[tpl.key]}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const MessageTemplateSettings = () => {
  const dispatch = useAppDispatch();
  const [templateGroups, setTemplateGroups] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const apiResponse = await new Promise((resolve, reject) => {
          dispatch(
            getMsgTemplatesApi({
              finalCallback: () => {},
              successCallback: (data) => resolve(data),
              failureCallback: (error) => reject(error),
            })
          );
        });

        const fetchedGroups = apiResponse.templateGroups || [];
        setTemplateGroups(fetchedGroups);

        const initialValues = {};
        fetchedGroups.forEach(group => {
          group.templates.forEach(template => {
            initialValues[template.key] = template.value || "";
          });
        });
        setValues(initialValues);

      } catch {
        // handle silently
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [dispatch]);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    
    const templatesToSave = Object.keys(values).map(key => ({
      key: key,
      template: values[key]
    }));

    try {
      await new Promise((resolve, reject) => {
        dispatch(
          updateMsgTemplatesApi({
            params: templatesToSave,
            finalCallback: resolve,
            successCallback: () => {
              setSaved(true);
              setTimeout(() => setSaved(false), 3000);
            },
            failureCallback: reject,
          })
        );
      });
    } catch {
      // handle silently
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mts-loading">
        <span className="mts-loading__spinner" />
      </div>
    );
  }

  return (
    <div className="mts">

      {/* ── Fixed header — never scrolls ── */}
      <div className="mts-header">
        <div className="mts-header__text">
          <h1 className="mts-header__title">Message Templates</h1>
          <p className="mts-header__subtitle">
            Customize the default messages used by the AI assistant.
          </p>
        </div>
        <button
          className={`mts-save-btn${saving ? " mts-save-btn--saving" : ""}${saved ? " mts-save-btn--saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : saved ? "Saved ✓" : "Save Templates"}
        </button>
      </div>

      {/* ── Scrollable content — only this moves ── */}
      <div className="mts-groups">
        {templateGroups.map((group, gi) => (
          <GroupSection
            key={group.title}
            group={group}
            groupIndex={gi}
            values={values}
            onChange={handleChange}
          />
        ))}
      </div>

    </div>
  );
};

export default MessageTemplateSettings;